import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import logger from '../utils/logger';

interface CacheEntry {
  id: number;
  input_text: string;
  input_text_lower: string;
  context?: string;
  provider: string;
  model?: string;
  classified_result: any;
  created_at: string;
  updated_at: string;
  last_used_at: string;
  use_count: number;
}

const CacheManager: React.FC = () => {
  const [cacheEntries, setCacheEntries] = useState<CacheEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<CacheEntry | null>(null);
  const [editText, setEditText] = useState('');
  const [editContext, setEditContext] = useState('');
  const [editResult, setEditResult] = useState('');

  const loadCache = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getCachedClassifications(100, 0);
      setCacheEntries(result.rows);
      setTotal(result.total);
      logger.info('Loaded cache entries:', result.rows.length);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to load cache';
      setError(errorMessage);
      logger.error('Error loading cache:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCache();
  }, []);

  const handleEdit = (entry: CacheEntry) => {
    setEditingEntry(entry);
    setEditText(entry.input_text);
    setEditContext(entry.context || '');
    setEditResult(JSON.stringify(entry.classified_result, null, 2));
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;

    try {
      let parsedResult;
      try {
        parsedResult = JSON.parse(editResult);
      } catch {
        setError('Invalid JSON in classified result');
        return;
      }

      await apiService.updateCachedClassification(
        editingEntry.id,
        editText,
        editContext || undefined,
        parsedResult
      );

      setEditingEntry(null);
      setError(null);
      await loadCache();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to update cache entry';
      setError(errorMessage);
      logger.error('Error updating cache entry:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this cache entry?')) {
      return;
    }

    try {
      await apiService.deleteCachedClassification(id);
      setError(null);
      await loadCache();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to delete cache entry';
      setError(errorMessage);
      logger.error('Error deleting cache entry:', err);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear ALL cache entries? This cannot be undone.')) {
      return;
    }

    try {
      const result = await apiService.clearAllCache();
      setError(null);
      logger.info(`Cleared ${result.deletedCount} cache entries`);
      await loadCache();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to clear cache';
      setError(errorMessage);
      logger.error('Error clearing cache:', err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Admin Cache Data
          </h2>
          <div className="flex space-x-3">
            <button
              onClick={loadCache}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Refresh
            </button>
            <button
              onClick={handleClearAll}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Clear All Cache
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Total cached entries:</strong> {total} | <strong>Cache hit rate:</strong> Improves performance by avoiding redundant AI calls
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="80" strokeDashoffset="60"></circle>
            </svg>
          </div>
        ) : cacheEntries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No cache entries found. Cache will be populated as classifications are performed.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Input Text
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider/Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Result
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cacheEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{entry.input_text}</div>
                      {entry.context && (
                        <div className="text-xs text-gray-500 mt-1">Context: {entry.context}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{entry.provider}</div>
                      {entry.model && (
                        <div className="text-xs text-gray-500">{entry.model}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-600 max-w-xs truncate">
                        {JSON.stringify(entry.classified_result)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">Used: {entry.use_count}x</div>
                      <div className="text-xs text-gray-500">
                        Last: {new Date(entry.last_used_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => handleEdit(entry)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit Modal */}
        {editingEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold mb-4">Edit Cache Entry</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Input Text
                  </label>
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Context (optional)
                  </label>
                  <input
                    type="text"
                    value={editContext}
                    onChange={(e) => setEditContext(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Classified Result (JSON)
                  </label>
                  <textarea
                    value={editResult}
                    onChange={(e) => setEditResult(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setEditingEntry(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CacheManager;

